import React from 'react';
import {
  TrendUp,
  TrendDown,
  Minus,
  ArrowsClockwise,
  GearSix,
  TrashSimple,
  Sparkle,
  CheckCircle,
  HourglassMedium,
} from '@phosphor-icons/react';

const InvestmentCard = ({
  investment,
  onStockTitleClick,
  onDeleteClick,
  onUpdateSettingsClick,
}) => {
  if (!investment) return null;

  const lastStep =
    investment.steps && investment.steps.length > 0
      ? investment.steps[investment.steps.length - 1]
      : null;

  const percentageChange =
    lastStep && lastStep.buyed_price > 0
      ? ((investment.last_stock_price - lastStep.buyed_price) /
          lastStep.buyed_price) *
        100
      : 0;

  const isUp = percentageChange > 0;
  const isDown = percentageChange < 0;

  const netProfit = investment.net_profit ?? 0;
  const totalProfit = investment.total_profit ?? 0;
  const beginBudget = investment.begin_budget || 0;
  const currentBalance = investment.current_balance || 0;
  const allocated = beginBudget - currentBalance;

  const balanceRatio =
    beginBudget > 0 ? ((currentBalance * 100) / beginBudget).toFixed(1) : 0;
  const allocatedRatio =
    beginBudget > 0 ? ((allocated * 100) / beginBudget).toFixed(1) : 0;

  return (
    <div className="investment-card">
      {/* Top Header */}
      <div className="card-top-bar">
        <div
          className="stock-identity-group"
          onClick={() => onStockTitleClick(investment.stock.ticker)}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onStockTitleClick(investment.stock.ticker);
            }
          }}
          role="button"
          tabIndex="0"
          title="Click to view AI Analysis & Checklist"
        >
          <div className="stock-title-row">
            <span className="stock-name">{investment.stock.name}</span>
            <span className="ticker-badge">{investment.stock.ticker}</span>
          </div>
          <div className="stock-badges">
            {investment.auto_restart && (
              <span
                className="status-chip auto-restart"
                title="Auto-restart enabled upon full exit"
              >
                <ArrowsClockwise size={11} weight="bold" />
                Auto
              </span>
            )}
            <span className="status-chip ai-insight">
              <Sparkle size={11} weight="fill" />
              AI Report
            </span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="card-action-group">
          <button
            onClick={e => {
              e.stopPropagation();
              onUpdateSettingsClick(
                investment.stock.ticker,
                investment.exit_condition,
                investment.auto_restart
              );
            }}
            className="action-icon-btn"
            title="Update exit condition or auto-restart"
            aria-label="Update settings"
          >
            <GearSix size={16} weight="bold" />
          </button>
          <button
            onClick={e => {
              e.stopPropagation();
              onDeleteClick(investment.stock.ticker);
            }}
            className="action-icon-btn danger"
            title="Delete this investment"
            aria-label="Delete investment"
          >
            <TrashSimple size={16} weight="bold" />
          </button>
        </div>
      </div>

      {/* Hero Stats Ribbon */}
      <div className="stock-price-ribbon">
        <div className="current-price-block">
          <span className="price-label">Current Price</span>
          <div className="price-value-row">
            <span className="current-price mono">
              {investment.last_stock_price?.toLocaleString()}
            </span>
            <span className="price-currency">KRW</span>
            <span
              className={`price-trend-badge ${
                isUp ? 'positive' : isDown ? 'negative' : 'neutral'
              }`}
            >
              {isUp ? (
                <TrendUp size={12} weight="bold" />
              ) : isDown ? (
                <TrendDown size={12} weight="bold" />
              ) : (
                <Minus size={12} weight="bold" />
              )}
              <span className="mono">
                {isUp ? '+' : ''}
                {percentageChange.toFixed(2)}%
              </span>
            </span>
          </div>
        </div>

        <div className="exit-target-pill" title="Target exit profit condition">
          <span className="exit-label">Target Exit</span>
          <span className="exit-val mono">+{investment.exit_condition}%</span>
        </div>
      </div>

      {/* Key Metrics Bento */}
      <div className="card-metrics-grid">
        <div className="card-metric-cell">
          <span className="cell-label">Budget</span>
          <span className="cell-value mono">
            {beginBudget.toLocaleString()}
          </span>
        </div>
        <div className="card-metric-cell">
          <span className="cell-label">Available Balance</span>
          <div className="cell-value-sub mono">
            <span>{currentBalance.toLocaleString()}</span>
            <span className="cell-ratio">({balanceRatio}%)</span>
          </div>
        </div>
        <div className="card-metric-cell">
          <span className="cell-label">Allocated</span>
          <div className="cell-value-sub mono">
            <span>{allocated.toLocaleString()}</span>
            <span className="cell-ratio">({allocatedRatio}%)</span>
          </div>
        </div>
        <div className="card-metric-cell">
          <span className="cell-label">Net Profit (Unrealized)</span>
          <span
            className={`cell-value mono ${
              netProfit > 0
                ? 'text-success'
                : netProfit < 0
                  ? 'text-danger'
                  : ''
            }`}
          >
            {netProfit > 0 ? '+' : ''}
            {netProfit.toLocaleString()}
          </span>
        </div>
        <div className="card-metric-cell full-width">
          <span className="cell-label">Cumulative Realized Profit</span>
          <span
            className={`cell-value mono ${
              totalProfit > 0
                ? 'text-success'
                : totalProfit < 0
                  ? 'text-danger'
                  : ''
            }`}
          >
            {totalProfit > 0 ? '+' : ''}
            {totalProfit.toLocaleString()} KRW
          </span>
        </div>
      </div>

      {/* Split Step Table */}
      <div className="steps-container">
        <div className="steps-header-bar">
          <span className="steps-title">Split Steps</span>
          <span className="steps-count-badge mono">
            {investment.steps?.filter(s => s.buyed_price > 0).length || 0}/
            {investment.steps?.length || 7} Active
          </span>
        </div>

        <div className="steps-table-wrapper">
          <table className="steps-table">
            <thead>
              <tr>
                <th>Step</th>
                <th>Status</th>
                <th>Buy Price</th>
                <th>Target</th>
                <th>Qty</th>
                <th>Exp. Profit</th>
              </tr>
            </thead>
            <tbody>
              {investment.steps?.map(step => {
                const isBought = step.buyed_price > 0;
                const profitAmount =
                  isBought && step.target_price > step.buyed_price
                    ? (step.target_price - step.buyed_price) * step.stock_count
                    : 0;
                const profitRate =
                  isBought && step.buyed_price > 0
                    ? ((step.target_price - step.buyed_price) /
                        step.buyed_price) *
                      100
                    : 0;

                return (
                  <tr
                    key={step.idx}
                    className={
                      isBought ? 'step-row-active' : 'step-row-pending'
                    }
                    title={
                      isBought && step.buyed_at
                        ? `Executed: ${new Date(step.buyed_at).toLocaleString()}`
                        : 'Waiting for trigger'
                    }
                  >
                    <td>
                      <span className="step-idx-badge mono">
                        {step.idx + 1}
                      </span>
                    </td>
                    <td>
                      {isBought ? (
                        <span className="step-status-chip bought">
                          <CheckCircle size={12} weight="fill" />
                          Bought
                        </span>
                      ) : (
                        <span className="step-status-chip waiting">
                          <HourglassMedium size={12} weight="bold" />
                          Waiting
                        </span>
                      )}
                    </td>
                    <td className="mono text-right">
                      {isBought ? step.buyed_price.toLocaleString() : '—'}
                    </td>
                    <td className="mono text-right">
                      {isBought ? step.target_price.toLocaleString() : '—'}
                    </td>
                    <td className="mono text-right">
                      {isBought ? step.stock_count : '—'}
                    </td>
                    <td className="mono text-right">
                      {isBought ? (
                        <span className="exp-profit-val text-success">
                          +{profitAmount.toLocaleString()}{' '}
                          <span className="profit-rate">
                            ({profitRate.toFixed(1)}%)
                          </span>
                        </span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default InvestmentCard;
